import { normalizeText, parseImportNumber } from './importer';
import { normalizeIngredientName } from './recipeProposal';
import { parseUnit } from './importCatalog';
import { isPurchaseUnit, UNITS, type PurchaseUnit } from './units';

/**
 * Lo que Alexia recuerda de las correcciones del usuario (fase 7, 12 oct
 * 2026). Alex: «hoy Alexia NO recuerda las correcciones de facturas».
 *
 * Dos memorias, las dos por cuenta:
 *
 *  1. ALIAS DE INGREDIENTE: «Tomate saladette» en la factura de Distribuciones
 *     del Norte es tu «Jitomate». Se guarda DENTRO del ingrediente
 *     (`Ingredient.aliases`), así viaja con el catálogo a todos los sitios
 *     donde se empareja (escáner, Excel, dictado) sin cargar nada más, y se
 *     ve y se borra en su ficha. Puede ir atado a un proveedor: lo que un
 *     proveedor llama «crema» puede no ser lo que otro llama igual.
 *  2. «NO ES UN INGREDIENTE»: las bolsas de basura de ese proveedor van solo
 *     al gasto. No tienen ingrediente al que colgarse, así que van aparte
 *     (`NoIngredientRule`, una por línea recordada).
 *
 * Las dos se comparan con el nombre normalizado de `normalizeIngredientName`
 * (sin acentos, mayúsculas, plurales ni «de»), el mismo que usa el
 * emparejador: «crema acida» y «Crema ácida» son la misma clave.
 */

export type AliasSource = 'factura' | 'excel' | 'dictado';

export interface IngredientAlias {
  /** Tal y como lo escribió el proveedor, el Excel o el usuario al dictar. */
  text: string;
  /** Solo para ese proveedor (líneas de factura). Sin él, vale para todos. */
  supplierId?: string;
  source: AliasSource;
  createdAt: string;
}

export interface NoIngredientRule {
  text: string;
  /** Sin proveedor, vale para cualquier factura. */
  supplierId?: string;
  createdAt: string;
}

/** Máximo de alias por ingrediente: es una memoria, no un diccionario. */
export const MAX_ALIASES = 30;

export const aliasKey = (text: string): string => normalizeIngredientName(text);

const mismoAlcance = (a: { supplierId?: string }, b: { supplierId?: string }): boolean =>
  (a.supplierId ?? '') === (b.supplierId ?? '');

/**
 * Añade un alias a la lista de un ingrediente: sin duplicados (misma clave y
 * mismo proveedor) y nunca el propio nombre del ingrediente, que ya empareja
 * solo. Devuelve la lista nueva, o la misma si no hay nada que añadir.
 */
export function withAlias(
  ingredient: { name: string; aliases?: IngredientAlias[] },
  alias: IngredientAlias,
): IngredientAlias[] {
  const actuales = ingredient.aliases ?? [];
  const clave = aliasKey(alias.text);
  if (!clave || clave === aliasKey(ingredient.name)) return actuales;
  const resto = actuales.filter((a) => !(aliasKey(a.text) === clave && mismoAlcance(a, alias)));
  return [...resto, { ...alias, text: alias.text.trim().slice(0, 120) }].slice(-MAX_ALIASES);
}

/** Quita el alias de esa clave y ese proveedor (o de cualquier proveedor si no se dice). */
export function withoutAlias(
  aliases: IngredientAlias[] | undefined,
  text: string,
  supplierId?: string | null,
): IngredientAlias[] {
  const clave = aliasKey(text);
  return (aliases ?? []).filter((a) => !(aliasKey(a.text) === clave && (supplierId === undefined || (a.supplierId ?? '') === (supplierId ?? ''))));
}

/**
 * ¿Alguien de este catálogo tiene ya este alias, en este alcance? Al recordar
 * un alias nuevo hay que quitárselo al anterior: «No es este» → elegir otro
 * significa que el alias viejo estaba mal.
 */
export function ingredientsWithAlias<T extends { ingredientId: string; aliases?: IngredientAlias[] }>(
  catalog: T[],
  text: string,
  supplierId?: string,
): T[] {
  const clave = aliasKey(text);
  return catalog.filter((ing) => (ing.aliases ?? []).some((a) => aliasKey(a.text) === clave && (a.supplierId ?? '') === (supplierId ?? '')));
}

/**
 * Unir proveedores: los alias que eran del que desaparece pasan al que se
 * queda, sin duplicar uno que ya tuviera con la misma clave. Devuelve la
 * misma lista si no hay nada que cambiar.
 */
export function aliasesDeProveedorUnido(
  aliases: IngredientAlias[] | undefined,
  fromId: string,
  intoId: string,
): IngredientAlias[] | undefined {
  if (!aliases?.some((a) => a.supplierId === fromId)) return aliases;
  const out: IngredientAlias[] = [];
  for (const a of aliases) {
    const alias = a.supplierId === fromId ? { ...a, supplierId: intoId } : a;
    const clave = aliasKey(alias.text);
    const repetido = out.findIndex((o) => aliasKey(o.text) === clave && mismoAlcance(o, alias));
    if (repetido < 0) out.push(alias);
    // Ya estaba (del que se queda, o repetido): se queda el primero.
  }
  return out;
}

/** Unir proveedores: las reglas del que desaparece, reescritas al que se queda. */
export function reglasDeProveedorUnido<T extends Pick<NoIngredientRule, 'text' | 'supplierId'>>(
  rules: T[],
  fromId: string,
  intoId: string,
): { remove: T[]; add: T[] } {
  const remove = rules.filter((r) => r.supplierId === fromId);
  const yaEstan = new Set(rules.filter((r) => r.supplierId === intoId).map((r) => aliasKey(r.text)));
  const add = remove.filter((r) => !yaEstan.has(aliasKey(r.text))).map((r) => ({ ...r, supplierId: intoId }));
  return { remove, add };
}

/** «No es un ingrediente» recordado: el de ese proveedor, o uno sin proveedor. */
export function matchesNoIngredientRule(
  name: string,
  rules: Array<Pick<NoIngredientRule, 'text' | 'supplierId'>>,
  supplierId?: string,
): boolean {
  const clave = aliasKey(name);
  if (!clave) return false;
  return rules.some((r) => aliasKey(r.text) === clave && (!r.supplierId || r.supplierId === supplierId));
}

/* ------------------------------------------------------------------ */
/* «No es un ingrediente»: lo que una factura de cocina trae y no se    */
/* cocina                                                              */
/* ------------------------------------------------------------------ */

export type NoIngredientReason = 'limpieza' | 'gas' | 'servicio' | 'envio' | 'desechable' | 'otros';

const REGLAS_NO_INGREDIENTE: Array<{ motivo: NoIngredientReason; re: RegExp }> = [
  // «Agua con gas» o «refresco sin gas» SÍ son de cocina: solo el gas de la estufa.
  { motivo: 'gas', re: /\b(gas (lp|l p|natural|butano|propano|estacionario)|cilindro( de)? gas|tanque( de)? gas|recarga( de)? gas|butano|propano)\b|^gas\b(?! (con|sin)\b)/ },
  { motivo: 'limpieza', re: /\b(detergentes?|lavavajillas|lavatrastes|lavaloza|jabon (para )?(trastes|platos|manos)|cloro|lejia|desengrasantes?|desinfectantes?|sanitizantes?|suavizantes?|fabuloso|pinol|limpiador(es)?|limpia ?vidrios|multiusos|fibras?( verdes?)?|estropajos?|escobas?|trapeadores?|jergas?|cubetas? de limpieza|guantes?|papel higienico|toallas? (de papel|interdobladas|en rollo)|bolsas? (de |para )?basura|bolsas? negras?|bolsas? jumbo)\b/ },
  { motivo: 'servicio', re: /\b(servicios? (a domicilio|tecnicos?|de mantenimiento)|cargo por servicio|mano de obra|mantenimiento|renta( de)?|alquiler|comision(es)?|propinas?)\b/ },
  { motivo: 'envio', re: /\b(envios?|costo de envio|cargo de envio|fletes?|transportes?|portes|reparto|entrega a domicilio|paqueteria)\b/ },
  // Lo que ya se ignoraba antes de la fase 7 (descuentos, totales, envases, papelería).
  { motivo: 'otros', re: /^(i v a|redondeo|anticipo|deposito)\b|\b(iva|subtotal|total|descuentos?|papeleria|envases?)\b/ },
];

/**
 * Clasifica una línea de factura que NO es un ingrediente (bolsas de basura,
 * gas, limpieza, servicio, envío…): va solo al gasto, nunca a Ingredientes.
 * Decidido con Alex el 10 oct. Devuelve el motivo, o undefined si puede ser
 * comida. Es conservadora a propósito: una línea de comida marcada como «no
 * es un ingrediente» se pierde sin que nadie lo vea; al revés, el usuario
 * solo tiene que tocar «No es un ingrediente».
 */
export function noEsIngrediente(description: string): NoIngredientReason | undefined {
  const texto = normalizeText(description);
  if (!texto) return undefined;
  return REGLAS_NO_INGREDIENTE.find((r) => r.re.test(texto))?.motivo;
}

/* ------------------------------------------------------------------ */
/* Cantidad comprada: «6 botellas de 1 L»                              */
/* ------------------------------------------------------------------ */

const ENVASES: Record<string, string> = {
  botella: 'botellas', bolsa: 'bolsas', frasco: 'frascos', bote: 'botes', caja: 'cajas',
  saco: 'sacos', lata: 'latas', garrafa: 'garrafas', garrafon: 'garrafones', paquete: 'paquetes',
  bulto: 'bultos', cubeta: 'cubetas', galon: 'galones', charola: 'charolas', costal: 'costales',
  tarro: 'tarros', bidon: 'bidones', pieza: 'piezas', manojo: 'manojos', rollo: 'rollos',
};
const ETIQUETA_ENVASE: Record<string, string> = { garrafon: 'garrafón', galon: 'galón', bidon: 'bidón' };

export interface InvoiceLineFormat {
  /** Cuántos compró: el 6 de «6 botellas». */
  count?: number;
  /** Lo que trae cada uno: 1 L. */
  packQty?: number;
  packUnit?: PurchaseUnit;
  /** «botella», si la factura lo dice. */
  envase?: string;
  /** Lo que pesa cada pieza de un paquete: «CHAPATA 90G PAQ 6U» = paquete de 6 de 90 g c/u (14 oct 2026). */
  pieza?: { qty: number; unit: PurchaseUnit };
}

/**
 * Lee de una línea de factura cuánto se compró y en qué formato: «ACEITE
 * AGUACATE 1LT» con cantidad 6 son 6 de 1 L. «PAN 140G C/46U» es una caja de
 * 46 × 140 g. Solo lo que está escrito: sin tamaño en la descripción no hay
 * formato, y el usuario lo pone al revisar.
 */
export function formatoDeLinea(description: string | undefined, quantity: string | undefined): InvoiceLineFormat {
  // Como normalizeText, pero sin perder el punto decimal: «1,1L» son 1.1 L.
  const texto = String(description ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/[^a-z0-9.\s]/g, ' ')
    .replace(/(\d)\.(?=\d)/g, '$1\u0000').replace(/\./g, ' ').replace(/\u0000/g, '.')
    .replace(/\s+/g, ' ').trim();
  const salida: InvoiceLineFormat = {};
  const cantidad = parseImportNumber(String(quantity ?? '').replace(/[a-z]+/gi, ' ').trim().split(/\s+/)[0]);
  if (cantidad != null && cantidad > 0) salida.count = cantidad;

  /*
   * Bloque S (escáner único, 14 oct 2026): «PAQ 6U», «PAQ 8», «PQT 12 PZ»,
   * «PAQUETE 6»: un paquete de 6 piezas. Manda sobre el tamaño de cada pieza
   * («CHAPATA 90G PAQ 6U» se compra por paquete de 6, no por 540 g): es lo
   * que dice la factura y lo que el cocinero cuenta.
   */
  const tam =/(?:^|\s)(\d+(?:\.\d+)?)\s*(kgs?|kilos?|g|gr|grs|gramos?|ml|cl|l|lt|lts|litros?|lb|lbs|gal|gals|fl ?oz|oz)(?=\s|$)/.exec(texto);
  // «PAQ 6U» / «PAQUETE DE 6 UNIDADES» y al revés, «6 UDS/PAQUETE», «8 U X PAQ» (revisión del 14 oct 2026).
  const paq = /(?:^|\s)(?:paq|paqs|pqt|pq|paquete|paquetes)\s?(?:de\s)?(\d{1,3})\s?(?:u|un|uds?|unidades|pz|pzs|pzas?|piezas?)?(?=\s|$)/.exec(texto)
    ?? /(?:^|\s)(\d{1,3})\s?(?:u|un|uds?|unidades|pz|pzs|pzas?|piezas?)\s?(?:x|por|en)?\s?(?:paq|paqs|pqt|pq|paquete)(?=\s|$)/.exec(texto);
  if (paq && Number(paq[1]) > 0) {
    salida.packQty = Number(paq[1]);
    salida.packUnit = 'pz';
    salida.envase = 'paquete';
    // «CHAPATA 90G PAQ 6U»: un paquete de 6, de 90 g cada una.
    const unidadPieza = tam ? parseUnit(tam[2]) : undefined;
    if (tam && isPurchaseUnit(unidadPieza) && Number(tam[1]) > 0) salida.pieza = { qty: Number(tam[1]), unit: unidadPieza };
    return salida;
  }

  if (tam) {
    const unidad = parseUnit(tam[2]);
    const qty = Number(tam[1]);
    if (isPurchaseUnit(unidad) && qty > 0) {
      // «C/46U», «46 x», «X6»: una caja con varias piezas de ese tamaño.
      const mult = /\bc (\d+) ?u?\b|\b(\d+) ?x\b|\bx ?(\d+)\b/.exec(texto.replace(tam[0].trim(), ' '));
      const veces = mult ? Number(mult[1] ?? mult[2] ?? mult[3]) : 1;
      salida.packQty = Math.round(qty * (veces > 1 ? veces : 1) * 1000) / 1000;
      salida.packUnit = unidad;
      if (veces > 1) salida.envase = 'caja';
    }
  } else {
    // «HUEVO C/30U» sin tamaño: una caja de 30 piezas (bloque S).
    const caja = /(?:^|\s)c (\d{1,3}) ?(?:u|uds?|pz|pzs)(?=\s|$)/.exec(texto);
    if (caja && Number(caja[1]) > 1) {
      salida.packQty = Number(caja[1]);
      salida.packUnit = 'pz';
      salida.envase = 'caja';
    }
  }
  if (!salida.envase) {
    const palabra = texto.split(' ').map((w) => w.replace(/(es|s)$/, '')).find((w) => w in ENVASES || `${w}e` in ENVASES);
    if (palabra) salida.envase = palabra in ENVASES ? palabra : `${palabra}e`;
  }
  return salida;
}

/** «6 botellas de 1 L», «2 bolsas de 1 kg», «10 kg», «6 × 1 L». Vacío si no hay nada que decir. */
export function fraseCantidadComprada(f: InvoiceLineFormat & { unit?: PurchaseUnit }): string {
  const n = (x: number) => String(Math.round(x * 1000) / 1000);
  const etiqueta = (u: PurchaseUnit) => UNITS[u].label;
  if (f.packQty && f.packUnit) {
    // 6440 g se lee mejor como 6.44 kg.
    const grande = f.packQty >= 1000 && (f.packUnit === 'g' || f.packUnit === 'ml');
    // «Paquete de 6», no «paquete de 6 pz» (bloque S).
    const cu = f.pieza ? ` (${n(f.pieza.qty)} ${etiqueta(f.pieza.unit)} c/u)` : '';
    const formato = f.packUnit === 'pz' && f.envase ? `${n(f.packQty)}${cu}`
      : grande ? `${n(f.packQty / 1000)} ${etiqueta(f.packUnit === 'g' ? 'kg' : 'l')}` : `${n(f.packQty)} ${etiqueta(f.packUnit)}`;
    if (!f.count || (f.count === 1 && !f.envase)) return f.envase ? `${ETIQUETA_ENVASE[f.envase] ?? f.envase} de ${formato}` : formato;
    const envase = f.envase ? (f.count === 1 ? ETIQUETA_ENVASE[f.envase] ?? f.envase : ENVASES[f.envase]) : undefined;
    return envase ? `${n(f.count)} ${envase} de ${formato}` : `${n(f.count)} × ${formato}`;
  }
  if (f.count && f.unit) return `${n(f.count)} ${f.unit === 'pz' ? (f.count === 1 ? 'pieza' : 'piezas') : etiqueta(f.unit)}`;
  if (f.count && f.envase) return `${n(f.count)} ${f.count === 1 ? ETIQUETA_ENVASE[f.envase] ?? f.envase : ENVASES[f.envase]}`;
  return '';
}
